import type { ReactNode } from 'react'
import type { ChartPane, ChartSeries } from '@/components/charts/time-chart'
import type { ChartRange } from '@/components/market/index-chart-card'
import type { DailySeries, LiveSpec } from '@/lib/market/types'

import { useCallback, useMemo, useState } from 'react'
import { ChartArea, ChartCandlestick, ChartSpline } from 'lucide-react'
import { cn } from '@/lib/utils'
import { dayTicks } from '@/components/charts/scales'
import { RANGE_OPTIONS, rangeStart } from '@/components/market/index-chart-card'
import { Segmented } from '@/components/market/segmented'
import { useLive, useQuote } from '@/components/market/live-provider'
import { bollinger, macd, rsi, sma } from '@/lib/market/indicators'
import { formatCompact, formatNumber, formatPrice } from '@/lib/market/format'
import { SESSION_STEPS, formatDay, sessionStepLabel, weekdayOf } from '@/lib/market/calendar'
import { sessionPathUntil, volumeShare } from '@/lib/market/live'
import Legend from '@/components/charts/legend'
import TimeChart from '@/components/charts/time-chart'

type ChartType = 'candles' | 'line' | 'area'
type LowerPane = 'none' | 'rsi' | 'macd'

interface StockChartProps {
    ticker: string
    series: DailySeries
    live: LiveSpec
    benchmark: number[]
}

interface Overlays {
    sma20: boolean
    sma50: boolean
    bollinger: boolean
    compare: boolean
}

const TYPES: Array<{ value: ChartType, label: ReactNode }> = [
    { value: 'candles', label: <span className='flex items-center gap-1.5'><ChartCandlestick aria-hidden='true' className='size-3.5' />Candles</span> },
    { value: 'line', label: <span className='flex items-center gap-1.5'><ChartSpline aria-hidden='true' className='size-3.5' />Line</span> },
    { value: 'area', label: <span className='flex items-center gap-1.5'><ChartArea aria-hidden='true' className='size-3.5' />Area</span> }
]

const LOWER: Array<{ value: LowerPane, label: string }> = [
    { value: 'none', label: 'Volume' },
    { value: 'rsi', label: 'RSI' },
    { value: 'macd', label: 'MACD' }
]

const OVERLAY_META: Array<{ key: keyof Overlays, label: string, color: string }> = [
    { key: 'sma20', label: 'SMA 20', color: 'var(--series-2)' },
    { key: 'sma50', label: 'SMA 50', color: 'var(--series-3)' },
    { key: 'bollinger', label: 'Bollinger 20, 2', color: 'var(--series-7)' },
    { key: 'compare', label: 'KASI rebased', color: 'var(--series-5)' }
]

const upColor = 'color-mix(in oklab, var(--up) 60%, transparent)'
const downColor = 'color-mix(in oklab, var(--down) 60%, transparent)'

const pick = <T,>(values: T[], indexes: number[]): T[] => indexes.map(index => values[index])

const StockChart = ({ ticker, series, live, benchmark }: StockChartProps): ReactNode => {
    const [range, setRange] = useState<ChartRange>('6M')
    const [type, setType] = useState<ChartType>('candles')
    const [lower, setLower] = useState<LowerPane>('none')
    const [overlays, setOverlays] = useState<Overlays>({ sma20: true, sma50: true, bollinger: false, compare: false })
    const { progress } = useLive()
    const quote = useQuote(live)

    const daily = useMemo(() => {
        const last = series.c.length - 1
        const o = series.o.slice()
        const h = series.h.slice()
        const l = series.l.slice()
        const c = series.c.slice()
        const v = series.v.slice()

        o[last] = quote.open
        h[last] = quote.high
        l[last] = quote.low
        c[last] = quote.last
        v[last] = quote.volume

        return { d: series.d, o, h, l, c, v }
    }, [series, quote])

    const indicators = useMemo(() => ({
        sma20: sma(daily.c, 20),
        sma50: sma(daily.c, 50),
        bands: bollinger(daily.c, 20, 2),
        rsi: rsi(daily.c, 14),
        macd: macd(daily.c)
    }), [daily.c])

    const view = useMemo(() => {
        if (range === '1D') {
            const points = sessionPathUntil(live.path, progress)
            const count = SESSION_STEPS + 1
            const close: Array<number | null> = Array.from({ length: count }, (_, step) => points[step] ?? null)
            const volume: Array<number | null> = Array.from({ length: count }, (_, step) => {
                if (step >= points.length) return null
                if (step === 0) return 0

                return Math.round(live.volume * (volumeShare(step / SESSION_STEPS) - volumeShare((step - 1) / SESSION_STEPS)))
            })

            return {
                intraday: true,
                weekly: false,
                count,
                labels: Array.from({ length: count }, (_, step) => `${sessionStepLabel(step)} EAT`),
                days: [] as number[],
                indexes: [] as number[],
                close,
                volume
            }
        }

        const start = rangeStart(daily.d, range)
        let indexes = Array.from({ length: daily.d.length - start }, (_, offset) => start + offset)
        const weekly = type === 'candles' && indexes.length > 260

        if (weekly) {
            const ends: number[] = []
            indexes.forEach((index, position) => {
                const next = indexes[position + 1]
                if (next === undefined || weekdayOf(daily.d[next]) < weekdayOf(daily.d[index])) ends.push(index)
            })
            indexes = ends
        }

        return {
            intraday: false,
            weekly,
            count: indexes.length,
            labels: indexes.map(index => (weekly ? `Week to ${formatDay(daily.d[index])}` : `${formatDay(daily.d[index], 'weekday')} ${formatDay(daily.d[index]).slice(-4)}`)),
            days: pick(daily.d, indexes),
            indexes,
            close: pick(daily.c, indexes) as Array<number | null>,
            volume: [] as Array<number | null>
        }
    }, [range, type, daily, live, progress])

    const panes: ChartPane[] = useMemo(() => {
        const result: ChartPane[] = []
        const priceSeries: ChartSeries[] = []

        if (view.intraday) {
            priceSeries.push({ kind: 'area', id: 'price', label: ticker, color: 'var(--series-1)', values: view.close })
        }
        else {
            const { indexes, weekly } = view

            if (overlays.bollinger) {
                priceSeries.push({
                    kind: 'band',
                    id: 'bollinger',
                    label: 'Bollinger band',
                    color: 'var(--series-7)',
                    upper: pick(indicators.bands.upper, indexes),
                    lower: pick(indicators.bands.lower, indexes)
                })
            }

            if (type === 'candles') {
                const open: number[] = []
                const high: number[] = []
                const low: number[] = []
                const close: number[] = []

                indexes.forEach((index, position) => {
                    if (weekly) {
                        const from = position === 0 ? Math.max(0, index - 4) : indexes[position - 1] + 1
                        let weekHigh = -Infinity
                        let weekLow = Infinity

                        for (let cursor = from; cursor <= index; cursor++) {
                            if (daily.h[cursor] > weekHigh) weekHigh = daily.h[cursor]
                            if (daily.l[cursor] < weekLow) weekLow = daily.l[cursor]
                        }

                        open.push(daily.o[from])
                        high.push(weekHigh)
                        low.push(weekLow)
                        close.push(daily.c[index])
                    }
                    else {
                        open.push(daily.o[index])
                        high.push(daily.h[index])
                        low.push(daily.l[index])
                        close.push(daily.c[index])
                    }
                })

                priceSeries.push({ kind: 'candles', id: 'price', label: ticker, open, high, low, close })
            }
            else {
                priceSeries.push({ kind: type === 'area' ? 'area' : 'line', id: 'price', label: ticker, color: 'var(--series-1)', values: view.close })
            }

            if (overlays.sma20) priceSeries.push({ kind: 'line', id: 'sma20', label: 'SMA 20', color: 'var(--series-2)', values: pick(indicators.sma20, indexes), width: 1.5 })
            if (overlays.sma50) priceSeries.push({ kind: 'line', id: 'sma50', label: 'SMA 50', color: 'var(--series-3)', values: pick(indicators.sma50, indexes), width: 1.5 })

            if (overlays.compare && indexes.length) {
                const first = indexes[0]
                const base = benchmark[first]
                const anchor = daily.c[first]
                const rebased = indexes.map(index => (base > 0 ? (benchmark[index] / base) * anchor : null))
                priceSeries.push({ kind: 'line', id: 'compare', label: 'KASI rebased', color: 'var(--series-5)', values: rebased, width: 1.5 })
            }
        }

        result.push({
            id: 'price',
            height: 300,
            format: formatPrice,
            series: priceSeries,
            guides: view.intraday ? [{ value: live.prevClose, label: 'Previous close' }] : [],
            marker: { value: quote.last, tone: quote.change > 0 ? 'up' : quote.change < 0 ? 'down' : 'flat' }
        })

        const volumeValues: Array<number | null> = view.intraday
            ? view.volume
            : view.indexes.map((index, position) => {
                if (!view.weekly) return daily.v[index]

                const from = position === 0 ? Math.max(0, index - 4) : view.indexes[position - 1] + 1
                let total = 0
                for (let cursor = from; cursor <= index; cursor++) total += daily.v[cursor]

                return total
            })

        result.push({
            id: 'volume',
            height: 78,
            label: 'Volume',
            format: value => formatCompact(value),
            series: [{
                kind: 'bars',
                id: 'volume',
                label: 'Volume',
                values: volumeValues,
                color: (position: number) => {
                    if (view.intraday) {
                        const current = view.close[position]
                        const previous = position > 0 ? view.close[position - 1] : live.prevClose

                        return current !== null && previous !== null && current < previous ? downColor : upColor
                    }

                    const index = view.indexes[position]
                    const previousIndex = position > 0 ? view.indexes[position - 1] : index - 1

                    return daily.c[index] >= (daily.c[previousIndex] ?? daily.o[index]) ? upColor : downColor
                },
                format: value => formatNumber(value)
            }],
            tickCount: 2
        })

        if (!view.intraday && lower === 'rsi') {
            result.push({
                id: 'rsi',
                height: 96,
                label: 'RSI 14',
                domain: [0, 100],
                format: value => formatNumber(value, 0),
                guides: [{ value: 70, label: 'Overbought 70' }, { value: 30, label: 'Oversold 30' }],
                series: [{ kind: 'line', id: 'rsi', label: 'RSI 14', color: 'var(--series-7)', values: pick(indicators.rsi, view.indexes), format: value => formatNumber(value, 1) }],
                tickCount: 2
            })
        }

        if (!view.intraday && lower === 'macd') {
            result.push({
                id: 'macd',
                height: 104,
                label: 'MACD 12, 26, 9',
                zero: true,
                format: value => formatNumber(value, 2),
                series: [
                    {
                        kind: 'bars',
                        id: 'histogram',
                        label: 'Histogram',
                        values: pick(indicators.macd.histogram, view.indexes),
                        color: (_position: number, value: number) => (value >= 0 ? upColor : downColor),
                        format: value => formatNumber(value, 3)
                    },
                    { kind: 'line', id: 'macd', label: 'MACD', color: 'var(--series-1)', values: pick(indicators.macd.macd, view.indexes), width: 1.5, format: value => formatNumber(value, 3) },
                    { kind: 'line', id: 'signal', label: 'Signal', color: 'var(--series-2)', values: pick(indicators.macd.signal, view.indexes), width: 1.5, format: value => formatNumber(value, 3) }
                ],
                tickCount: 2
            })
        }

        return result
    }, [view, overlays, indicators, type, daily, ticker, benchmark, live, quote.last, quote.change, lower])

    const xTicks = useCallback((width: number) => (view.intraday
        ? [0, 6, 18, 30, 42, 54, 66].map(step => ({ index: step, label: sessionStepLabel(step) }))
        : dayTicks(view.days, width)), [view])

    const first = view.close.find(value => value !== null) ?? null
    const rangeChange = !view.intraday && first ? ((quote.last - first) / first) * 100 : null
    const legendItems = view.intraday
        ? [{ key: 'price', label: `${ticker} intraday`, color: 'var(--series-1)', shape: 'line' as const }]
        : [
            ...(type === 'candles'
                ? [{ key: 'up', label: 'Up session', color: 'var(--up)', shape: 'rect' as const }, { key: 'down', label: 'Down session', color: 'var(--down)', shape: 'rect' as const }]
                : [{ key: 'price', label: ticker, color: 'var(--series-1)', shape: 'line' as const }]),
            ...OVERLAY_META.filter(item => overlays[item.key]).map(item => ({ key: item.key, label: item.label, color: item.color, shape: item.key === 'bollinger' ? 'rect' as const : 'line' as const }))
        ]

    return (
        <div>
            <div className='flex flex-wrap items-center gap-2'>
                <Segmented label='Chart range' options={RANGE_OPTIONS} value={range} onChange={setRange} />
                {range !== '1D' && <Segmented label='Chart type' options={TYPES} value={type} onChange={setType} />}
                {range !== '1D' && <Segmented label='Lower pane' options={LOWER} value={lower} onChange={setLower} />}
            </div>

            {range !== '1D' && (
                <div className='mt-2 flex flex-wrap items-center gap-1.5' role='group' aria-label='Chart overlays'>
                    {OVERLAY_META.map(item => (
                        <button
                            key={item.key}
                            type='button'
                            aria-pressed={overlays[item.key]}
                            onClick={() => setOverlays(current => ({ ...current, [item.key]: !current[item.key] }))}
                            className={cn(
                                'inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors',
                                overlays[item.key] ? 'border-axis bg-secondary text-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                            )}
                        >
                            <span aria-hidden='true' className='inline-block h-0.5 w-3 rounded-full' style={{ background: item.color, opacity: overlays[item.key] ? 1 : 0.4 }} />
                            {item.label}
                        </button>
                    ))}
                </div>
            )}

            <div className='mt-4 flex flex-wrap items-center justify-between gap-2'>
                <Legend items={legendItems} />
                <p className='text-xs text-muted-foreground'>
                    {view.weekly && <span className='mr-2'>Weekly candles</span>}
                    {rangeChange !== null && (
                        <>
                            Range change <span className={cn('font-medium tabular-nums', rangeChange >= 0 ? 'text-up' : 'text-down')}>{rangeChange >= 0 ? '+' : ''}{formatNumber(rangeChange, 2)}%</span>
                        </>
                    )}
                </p>
            </div>

            <div className='mt-2'>
                <TimeChart
                    count={view.count}
                    labels={view.labels}
                    xTicks={xTicks}
                    panes={panes}
                    ariaLabel={`${ticker} price chart, ${range} range. Use the left and right arrow keys to read each session.`}
                />
            </div>
        </div>
    )
}

export default StockChart
